import { Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { UserPermissionKeys } from 'app/security/authorization/user-permission-keys';
import { ReseedPermissionsService } from 'app/security/reseed-permissions.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { CONFIG } from '../../core/config';
import { ConfigService } from '../../core/config.service';
import { LicensingService } from '../../core/licensing/licensing.service';
import { PermissionsService } from '../../core/permissions.service';
import { FeatureService } from '../../features/feature.service';
import { PasswordResetDialogComponent } from '../../security/authentication/password-reset/password-reset-dialog.component';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { LicenceModules } from '../../viewmodels/LicenceModules';
import { SubMenuItem } from './sub-menu/sub-menu-item';

@Component({
  selector: 'hb-sidebar',
  templateUrl: './app-sidebar.component.html',
  styleUrls: ['./app-sidebar.component.css'],
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  private userPermissionKeys: UserPermissionKeys = new UserPermissionKeys();
  public hasIsSecurityAccess: boolean = false;
  // Manage Equipment
  public hasHierarchyReadAccess: boolean = false;
  public hasReportsAllReadAccess: boolean = false;
  // RBI
  public hasRbiRiskAnalysisModelRunReadAccess: boolean = false;
  public hasRbiRiskAnalysisInspectionHistoryReadAccess: boolean = false;
  public hasRbiMitigationsRecommentationReadAccess: boolean = false;
  public rbiRiskAnalysisMassCriticalityRead: boolean = false;
  public rbiRiskAnalysisMassCriticalityTemplatesRead: boolean = false;
  // Maintenance
  public hasMaintenanceMaintenanceStrategyReadAccess: boolean = false;
  public hasMaintenanceMaintenanceOptimizationReadAccess: boolean = false;
  // IDMS
  public hasIdmsInspectionReportReadAccess: boolean = false;
  public hasIdmsThicknessLocationReadAccess: boolean = false;
  public hasIdmsThicknessReportRead: boolean = false;
  // Build
  public hasBuildModelReadAccess: boolean = false;
  public hasBuildFunctionReadAccess: boolean = false;
  public hasBuildLookupReadAccess: boolean = false;
  // Admin
  public hasAdminOrganisationReadAccess: boolean = false;
  public hasAdminModelReadAccess: boolean = false;
  public hasAdminRiskMatrixReadAccess: boolean = false;
  public hasAdminHierarchyTemplateReadAccess: boolean = false;
  public hasAdminInspectionTemplateReadAccess: boolean = false;
  public hasAdminEventTemplateReadAccess: boolean = false;
  public hasAdminLookupReadAccess: boolean = false;
  public hasAdminMasterInspectionMethodReadAccess: boolean = false;
  public hasAdminUomReadAccess: boolean = false;
  public hasAdminMasterAttributeReadAccess: boolean = false;
  public hasAdminDataUploadExecute: boolean = false;
  public hasAdminMaintenanceReadAccess: boolean = false;
  public hasAdminModelManagerViewAccess: boolean = false;
  public hasAdminUpdateOperationsReadAccess: boolean = false;
  // License
  public hasLicenseViewAccess: boolean = false;

  private unsubscribe: Subject<boolean> = new Subject();

  // routes
  public readonly securityParentRoute: string = CONFIG.routes.security.parent;

  public securityMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.Users'),
      route: CONFIG.routes.securityNew.users,
      requiredPermission: UserPermissionKeys.isSecurity,
      linkId: 'security-new-users',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Roles'),
      route: CONFIG.routes.securityNew.roles,
      requiredPermission: UserPermissionKeys.isSecurity,
      linkId: 'security-new-roles',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.UserGroups'),
      route: CONFIG.routes.securityNew.userGroups,
      requiredPermission: UserPermissionKeys.isSecurity,
      linkId: 'security-new-user-groups',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.ReseedPermissions'),
      route: CONFIG.routes.securityNew.reseedPermission,
      requiredPermission: UserPermissionKeys.isSecurity,
      linkId: 'security-re-seed-permission',
      isPopupSubMenu: true,
    },
  ];

  public manageEquipmentMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.Hierarchy'),
      route: CONFIG.routes.hierarchy.parent,
      requiredPermission: this.userPermissionKeys.hierarchyHierarchyItemRead,
      linkId: 'manage-equipment-hierarchy',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Reports'),
      route: CONFIG.routes.manageEquipment.reports,
      requiredPermission: this.userPermissionKeys.reportsAllRead,
      linkId: 'manage-equipment-reports',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.LinkModels'),
      route: CONFIG.routes.manageEquipment.linkModels,
      requiredPermission: this.userPermissionKeys.hierarchyHierarchyItemUpdate,
      linkId: 'manage-equipment-link-models',
    },
  ];

  public rbiMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.RiskAnalysis'),
      route: CONFIG.routes.rbiRiskAnalysis.parent,
      requiredPermission: this.userPermissionKeys.rbiRiskAnalysisModelRunRead,
      linkId: 'rbi-risk-analysis',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.MassCriticalityTemplates'),
      route: CONFIG.routes.rbiRiskAnalysis.templates,
      requiredPermission: this.userPermissionKeys.rbiRiskAnalysisMassCriticalityTemplatesRead,
      linkId: 'rbi-mass-criticality-templates',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.MassCriticalityResults'),
      route: CONFIG.routes.rbiRiskAnalysis.results,
      requiredPermission: this.userPermissionKeys.rbiRiskAnalysisMassCriticalityRead,
      linkId: 'rbi-mass-criticality-results',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.RiskProjection'),
      route: CONFIG.routes.rbiRiskAnalysis.rbiProjection,
      requiredPermission: this.userPermissionKeys.rbiRiskAnalysisModelRunRead,
      linkId: 'rbi-risk-projection',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Mitigations'),
      route: CONFIG.routes.mo.recommendations.parent,
      requiredPermission: this.userPermissionKeys.rbiMitigationsRecommentationRead,
      linkId: 'rbi-mitigations',
    },
  ];

  public maintenanceMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.Strategies'),
      route: CONFIG.routes.mo.maintenanceStrategies,
      requiredPermission: this.userPermissionKeys.maintenanceMaintenanceStrategyRead,
      linkId: 'maintenance-strategy',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Optimization'),
      route: CONFIG.routes.mo.maintenanceOptimization,
      requiredPermission: this.userPermissionKeys.maintenanceMaintenanceOptimizationRead,
      linkId: 'maintenance-optimization',
    },
  ];

  public idmsMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.InspectionReports'),
      route: CONFIG.routes.idms.inspectionReports,
      requiredPermission: this.userPermissionKeys.idmsInspectionReportRead,
      linkId: 'idms-inspection-reports',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.ThicknessLocations'),
      route: CONFIG.routes.thicknessManagement.thicknessLocations,
      requiredPermission: this.userPermissionKeys.idmsThicknessLocationRead,
      linkId: 'idms-thickness-locations',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.ThicknessReports'),
      route: CONFIG.routes.thicknessManagement.thicknessReports,
      requiredPermission: this.userPermissionKeys.idmsThicknessReportRead,
      linkId: 'idms-thickness-reports',
    },
  ];

  public buildMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.Models'),
      route: CONFIG.routes.build.models,
      requiredPermission: this.userPermissionKeys.buildModelRead,
      linkId: 'build-models',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Functions'),
      route: CONFIG.routes.build.functions,
      requiredPermission: this.userPermissionKeys.buildFunctionRead,
      linkId: 'build-functions',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Nodes'),
      route: CONFIG.routes.build.libraryNodes,
      requiredPermission: this.userPermissionKeys.buildModelRead,
      linkId: 'build-libraryNodes',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Lookups'),
      route: CONFIG.routes.build.lookupsOld,
      queryParams: { builder: 'true' },
      requiredPermission: this.userPermissionKeys.buildLookupRead,
      linkId: 'build-lookups',
    },
  ];

  public adminMenuItems: SubMenuItem[] = [
    {
      name: this.i18NextPipe.transform('Sidebar.Organizations'),
      route: CONFIG.routes.admin.organisations,
      requiredPermission: this.userPermissionKeys.adminOrganisationRead,
      linkId: 'admin-organizations',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Models'),
      route: CONFIG.routes.admin.models,
      requiredPermission: this.userPermissionKeys.adminModelManagerView,
      linkId: 'admin-models',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.RBIRiskMatrix'),
      route: CONFIG.routes.admin.riskMatrix,
      requiredPermission: this.userPermissionKeys.adminRiskMatrixRead,
      linkId: 'admin-rbi-risk-matrix',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.DataUploads'),
      route: CONFIG.routes.admin.dataUpload,
      requiredPermission: this.userPermissionKeys.adminDataUploadExecute,
      linkId: 'admin-data-uploads',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.TableAttributeTemplates'),
      route: CONFIG.routes.admin.tableAttributeTemplate,
      requiredPermission: this.userPermissionKeys.adminHierarchyTemplateRead,
      linkId: 'admin-table-attribute-templates',
    },
    //  Commenting the HierarchyTemplate Old Menu,Once New Window is completly tested
    //  we can remove this piece of code from the list
    // {
    //   name: this.i18NextPipe.transform('Sidebar.HierarchyTemplates'),
    //   route: CONFIG.routes.admin.hierarchyTemplates,
    //   requiredPermission: this.userPermissionKeys.adminHierarchyTemplateRead,
    //   linkId: 'admin-hierarchy-templates',
    // },
    {
      name: this.i18NextPipe.transform('Sidebar.HierarchyTemplatesNew'),
      route: CONFIG.routes.admin.hierarchyTemplatesNew,
      requiredPermission: this.userPermissionKeys.adminHierarchyTemplateRead,
      linkId: 'admin-hierarchy-templates-new',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.InspectionTemplates'),
      route: CONFIG.routes.admin.inspectionReportTemplates,
      requiredPermission: this.userPermissionKeys.adminInspectionTemplateRead,
      linkId: 'admin-inspection-templates',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.EventTemplates'),
      route: CONFIG.routes.eventTemplate.parent,
      requiredPermission: this.userPermissionKeys.adminEventTemplateRead,
      linkId: 'admin-event-templates',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.AdminLookups'),
      route: CONFIG.routes.admin.lookups,
      requiredPermission: this.userPermissionKeys.adminLookupRead,
      linkId: 'admin-lookups',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.MasterInspectionMethod'),
      route: CONFIG.routes.admin.masterInspectionMethods,
      requiredPermission: this.userPermissionKeys.adminMasterInspectionMethodRead,
      linkId: 'admin-master-inspection-methods',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Maintenance'),
      route: null,
      requiredPermission: '',
      linkId: 'admin-maintenance',
      hasSubMenu: true,
      subMenuItems: [
        {
          name: this.i18NextPipe.transform('Sidebar.PlannedMaintenance'),
          route: CONFIG.routes.admin.maintenance.plannedMaintenance,
          requiredPermission: this.userPermissionKeys.adminMaintenancePlannedMaintenanceRead,
          linkId: 'admin-maintenance-planned-maintenance',
        },
        {
          name: this.i18NextPipe.transform('Sidebar.PerformanceStandards'),
          route: CONFIG.routes.admin.maintenance.performanceStandards,
          requiredPermission: this.userPermissionKeys.adminMaintenancePerformanceStandardRead,
          linkId: 'admin-maintenance-performance-standards',
        },
        {
          name: this.i18NextPipe.transform('Sidebar.MaintenanceStrategies'),
          route: CONFIG.routes.admin.maintenance.maintenanceStrategies,
          requiredPermission: this.userPermissionKeys.adminMaintenanceMaintenanceStrategyRead,
          linkId: 'admin-maintenance-strategies',
        },
      ],
    },
    {
      name: this.i18NextPipe.transform('Sidebar.Units'),
      route: CONFIG.routes.admin.units,
      requiredPermission: this.userPermissionKeys.adminUomRead,
      linkId: 'admin-units',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.MasterAttributes'),
      route: CONFIG.routes.admin.masterAttributes,
      requiredPermission: this.userPermissionKeys.adminMasterAttributeRead,
      linkId: 'admin-master-attributes',
    },
    {
      name: this.i18NextPipe.transform('Sidebar.UpdateOperations'),
      route: CONFIG.routes.admin.syncAlgorithms,
      requiredPermission: this.userPermissionKeys.adminUpdateOperationsRead,
      linkId: 'admin-update-operations',
    },
  ];

  public userAuthenticated: boolean;
  public loggedInUser: string;
  public dashboardURL;
  public isError: boolean = false;

  get usingLoginProvider(): boolean {
    return localStorage.getItem('usingLoginProvider') === 'true'
      || this.configService.getRemoteConfiguration().loginProvider == 'auth0';
  }

  constructor(
    private el: ElementRef,
    private permissionService: PermissionsService,
    private location: Location,
    private loginService: AuthService,
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private licenceService: LicensingService,
    private featureService: FeatureService,
    private i18NextPipe: I18NextPipe,
    private reseedPermissionsService: ReseedPermissionsService,
    private configService: ConfigService,
  ) { }

  public getPath(): string {
    return this.location.path();
  }

  public logout() {
    this.loginService.logout();
  }

  public resetPassword() {
    this.bsModalRef = this.modalService.show(PasswordResetDialogComponent);
  }

  public onopenPopupClicked(item: SubMenuItem): void {
    if (item.route === CONFIG.routes.securityNew.reseedPermission) {
      // Give enough time to close menu item.
      setTimeout(() => {
        this.seedPermissionEventTemplate();
      });
    }
  }

  private seedPermissionEventTemplate(): void {

    this.bsModalRef = this.modalService.show(ConfirmationDialogComponent);

    if (!this.bsModalRef) {
      return;
    }

    const content: ConfirmationDialogComponent = this.createTemplateSeedPermissionConfirmationDialog();

    content.afterClosed.pipe(takeUntil(this.unsubscribe)).subscribe(result => {
      if (result) {
        this.reseedPermissionsService.reseedPermissions();
      }
    });
  }

  private createTemplateSeedPermissionConfirmationDialog(): ConfirmationDialogComponent {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const content: ConfirmationDialogComponent = this.bsModalRef.content;

    content.showCancel = true;
    content.title = this.i18NextPipe.transform('ReseedPermission.Confirm');
    content.messageTop = this.i18NextPipe.transform('ReseedPermission.ConfirmationMessage');

    return content;
  }

  public buildItemActive(): boolean {
    return (
      (this.getPath().includes('/models') || this.getPath().includes('/functions') || this.getPath().includes('/lookups')) &&
      !this.getPath().includes('/admin')
    );
  }

  public rbiItemActive(): boolean {
    return (
      this.getPath().includes('/rbi') ||
      this.getPath().includes('/recommendations') ||
      this.getPath().includes('/massCriticalityTemplates') ||
      this.getPath().includes('/massCriticalityResults')
    );
  }

  public maintenanceItemActive(): boolean {
    return this.getPath().includes('/maintenance') || this.getPath().includes('/maintenance-optimization') || this.getPath().includes('/quick-scan');
  }

  public idmsItemActive(): boolean {
    return this.getPath().includes('/idms') || this.getPath().includes('/thickness-management');
  }

  public amItemActive(): boolean {
    const path = this.getPath();

    return (path.includes('/hierarchy') && !path.includes('/hierarchy-')) || path.includes('/reports') || path.includes('/assets/');
  }

  public adminItemActive(): boolean {
    return this.getPath().includes('/admin/');
  }

  public securityItemActive(): boolean {
    return this.getPath().includes(this.securityParentRoute);
  }

  // wait for the component to render completely
  public ngOnInit(): void {
    this.loginService.loggedIn.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.userAuthenticated = data;
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const nativeElement: HTMLElement = this.el.nativeElement;
    const parentElement: HTMLElement = nativeElement.parentElement;

    // move all children out of the element
    while (nativeElement.firstChild) {
      parentElement.insertBefore(nativeElement.firstChild, nativeElement);
    }

    // remove the empty element(the host)
    parentElement.removeChild(nativeElement);

    this.subscribeToLicenseChanges();
    this.subscribeToPermissionsChanges();

    this.configService.selectedCatalog.pipe(takeUntil(this.unsubscribe)).subscribe(catalogName => {
      this.dashboardURL = '/' + catalogName + '/' + CONFIG.routes.dashboard;
    });
    this.configService.IsError.pipe(takeUntil(this.unsubscribe)).subscribe(error => {
      this.isError = error;
    });
  }

  private subscribeToLicenseChanges(): void {
    this.licenceService.licence.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.initSidebarPermissionsWithLicenseRequiredOnly();
      this.updateSubMenuItemsAccessability(true);
    });
  }

  private subscribeToPermissionsChanges(): void {
    this.permissionService.userPermissionKeyValuePairs.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.initSidebarPermissions();
      this.updateSubMenuItemsAccessability();
    });
  }

  private initSidebarPermissions(): void {
    const permissionsKeys = new UserPermissionKeys();
    const {
      hierarchyHierarchyItemRead,
      reportsAllRead,
      buildModelRead,
      buildFunctionRead,
      buildLookupRead,
      adminOrganisationRead,
      adminModelRead,
      adminRiskMatrixRead,
      adminHierarchyTemplateRead,
      adminInspectionTemplateRead,
      adminEventTemplateRead,
      adminLookupRead,
      adminMasterInspectionMethodRead,
      adminUomRead,
      adminMasterAttributeRead,
      adminDataUploadExecute,
      adminMaintenancePlannedMaintenanceRead,
      adminMaintenanceMaintenanceStrategyRead,
      adminMaintenancePerformanceStandardRead,
      adminModelManagerView,
      licenceRead,
      adminUpdateOperationsRead,
    } = permissionsKeys;

    this.hasIsSecurityAccess = this.permissionService.checkUserHasPermissions(UserPermissionKeys.isSecurity);

    // Manage Equipment
    this.hasHierarchyReadAccess = this.permissionService.checkUserHasPermissions(hierarchyHierarchyItemRead);
    this.hasReportsAllReadAccess = this.permissionService.checkUserHasPermissions(reportsAllRead);

    // Build
    this.hasBuildModelReadAccess = this.permissionService.checkUserHasPermissions(buildModelRead);
    this.hasBuildFunctionReadAccess = this.permissionService.checkUserHasPermissions(buildFunctionRead);
    this.hasBuildLookupReadAccess = this.permissionService.checkUserHasPermissions(buildLookupRead);

    // Admin
    this.hasAdminOrganisationReadAccess = this.permissionService.checkUserHasPermissions(adminOrganisationRead);
    this.hasAdminModelReadAccess = this.permissionService.checkUserHasPermissions(adminModelRead);
    this.hasAdminRiskMatrixReadAccess = this.permissionService.checkUserHasPermissions(adminRiskMatrixRead);
    this.hasAdminHierarchyTemplateReadAccess = this.permissionService.checkUserHasPermissions(adminHierarchyTemplateRead);
    this.hasAdminInspectionTemplateReadAccess = this.permissionService.checkUserHasPermissions(adminInspectionTemplateRead);
    this.hasAdminEventTemplateReadAccess = this.permissionService.checkUserHasPermissions(adminEventTemplateRead);
    this.hasAdminLookupReadAccess = this.permissionService.checkUserHasPermissions(adminLookupRead);
    this.hasAdminMasterInspectionMethodReadAccess = this.permissionService.checkUserHasPermissions(adminMasterInspectionMethodRead);
    this.hasAdminUomReadAccess = this.permissionService.checkUserHasPermissions(adminUomRead);
    this.hasAdminMasterAttributeReadAccess = this.permissionService.checkUserHasPermissions(adminMasterAttributeRead);
    this.hasAdminDataUploadExecute = this.permissionService.checkUserHasPermissions(adminDataUploadExecute);
    this.hasAdminUpdateOperationsReadAccess = this.permissionService.checkUserHasPermissions(adminUpdateOperationsRead);

    this.hasAdminMaintenanceReadAccess =
      this.permissionService.checkUserHasPermissions(adminMaintenancePlannedMaintenanceRead) ||
      this.permissionService.checkUserHasPermissions(adminMaintenanceMaintenanceStrategyRead) ||
      (this.featureService.maintenanceStrategyEnabled() && this.permissionService.checkUserHasPermissions(adminMaintenancePerformanceStandardRead));

    this.hasAdminModelManagerViewAccess = this.permissionService.checkUserHasPermissions(adminModelManagerView);

    // License
    this.hasLicenseViewAccess = this.permissionService.checkUserHasPermissions(licenceRead);

    this.initSidebarPermissionsWithLicenseRequiredOnly();
  }

  private initSidebarPermissionsWithLicenseRequiredOnly(): void {
    const permissionsKeys = new UserPermissionKeys();
    const {
      rbiRiskAnalysisModelRunRead,
      rbiRiskAnalysisInspectionHistoryRead,
      rbiRiskAnalysisMassCriticalityRead,
      rbiRiskAnalysisMassCriticalityTemplatesRead,
      rbiMitigationsRecommentationRead,
      maintenanceMaintenanceOptimizationRead,
      maintenanceMaintenanceStrategyRead,
      idmsInspectionReportRead,
      idmsThicknessLocationRead,
      idmsThicknessReportRead,
    } = permissionsKeys;

    if (this.licenceService.isModuleLicenced(LicenceModules.Rbi)) {
      this.hasRbiRiskAnalysisModelRunReadAccess = this.permissionService.checkUserHasPermissions(rbiRiskAnalysisModelRunRead);
      this.hasRbiRiskAnalysisInspectionHistoryReadAccess = this.permissionService.checkUserHasPermissions(rbiRiskAnalysisInspectionHistoryRead);
      this.rbiRiskAnalysisMassCriticalityRead = this.permissionService.checkUserHasPermissions(rbiRiskAnalysisMassCriticalityRead);

      this.rbiRiskAnalysisMassCriticalityTemplatesRead =
        this.featureService.massCriticalityTemplatesEnabled() &&
        this.permissionService.checkUserHasPermissions(rbiRiskAnalysisMassCriticalityTemplatesRead);

      this.hasRbiMitigationsRecommentationReadAccess = this.permissionService.checkUserHasPermissions(rbiMitigationsRecommentationRead);
    }
    else {
      this.hasRbiRiskAnalysisModelRunReadAccess = this.hasRbiRiskAnalysisInspectionHistoryReadAccess = this.rbiRiskAnalysisMassCriticalityRead = this.rbiRiskAnalysisMassCriticalityTemplatesRead = this.hasRbiMitigationsRecommentationReadAccess = false;
    }

    if (this.licenceService.isModuleLicenced(LicenceModules.Rcm)) {
      this.hasMaintenanceMaintenanceStrategyReadAccess = this.permissionService.checkUserHasPermissions(maintenanceMaintenanceStrategyRead);
      this.hasMaintenanceMaintenanceOptimizationReadAccess = this.permissionService.checkUserHasPermissions(maintenanceMaintenanceOptimizationRead);
    }
    else {
      this.hasMaintenanceMaintenanceStrategyReadAccess = this.hasMaintenanceMaintenanceOptimizationReadAccess = false;
    }

    if (this.licenceService.isModuleLicenced(LicenceModules.Platform)) {
      this.hasIdmsInspectionReportReadAccess = this.permissionService.checkUserHasPermissions(idmsInspectionReportRead);
      this.hasIdmsThicknessLocationReadAccess = this.permissionService.checkUserHasPermissions(idmsThicknessLocationRead);
      this.hasIdmsThicknessReportRead = this.permissionService.checkUserHasPermissions(idmsThicknessReportRead);
    }
    else {
      this.hasIdmsInspectionReportReadAccess = this.hasIdmsThicknessLocationReadAccess = this.hasIdmsThicknessReportRead = false;
    }
  }

  private updateSubMenuItemsAccessability(onlyLicense: boolean = false): void {
    if (onlyLicense === false) {
      const menuItems = [...this.manageEquipmentMenuItems, ...this.buildMenuItems, ...this.adminMenuItems, ...this.securityMenuItems];

      this.updateSubMenuItemsArray(menuItems);
    }

    const menuItemsWithLicenseRequired = [...this.rbiMenuItems, ...this.maintenanceMenuItems, ...this.idmsMenuItems];

    this.updateSubMenuItemsArray(menuItemsWithLicenseRequired);
  }

  private updateSubMenuItemsArray(menuItems: SubMenuItem[]): void {
    menuItems.forEach(e => {
      if (e.hasSubMenu && e.subMenuItems && e.subMenuItems.length > 0) {
        e.subMenuItems.forEach(subMenu => {
          subMenu.showLink = this.permissionService.checkUserHasPermissions(subMenu.requiredPermission) && this.checkFeatureIsEnabled(subMenu.route);
        });
        e.showLink = e.subMenuItems.filter(subMenu => subMenu.showLink).length > 0;
      } else {
        e.showLink = this.permissionService.checkUserHasPermissions(e.requiredPermission) && this.checkFeatureIsEnabled(e.route);
      }
    });
  }

  private checkFeatureIsEnabled(route: string): boolean {
    const {
      routes: { admin, rbiRiskAnalysis, build },
    } = CONFIG;

    if (route === admin.maintenance.maintenanceStrategies) {
      return this.featureService.maintenanceStrategyEnabled();
    } else if (route === rbiRiskAnalysis.rbiProjection) {
      return this.featureService.riskProjectionEnabled();
    } else if (route === rbiRiskAnalysis.templates) {
      return this.featureService.massCriticalityTemplatesEnabled();
    } else if (route === build.libraryNodes) {
      return this.featureService.buildNodesEnabled();
    } else if (route === CONFIG.routes.securityNew.reseedPermission) {
      return this.featureService.permissionCacheEnabled();
    } else {
      return true;
    }
  }

  public onNavItemClick() {
    if (document.activeElement instanceof HTMLElement) {
      // force nav item to lose focus
      document.activeElement.blur();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }
}
